import type { ReactNode } from "react";
import { Box, Button, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export interface NavItem {
    key: string;
    label: string;
    icon: ReactNode;
    color?: "primary" | "secondary";
    onClick: () => void;
    disabled?: boolean;
}

interface Props {
    items: NavItem[];
    /** solo icone (desktop) */
    collapsed: boolean;
    /** se presente mostra il pulsante per comprimere/espandere */
    onToggleCollapsed?: () => void;
}

/** Menù laterale: pulsanti con etichetta oppure, compresso, sole icone con tooltip */
export default function NavMenu({ items, collapsed, onToggleCollapsed }: Props) {
    return (
        <Box sx={{ p: collapsed ? 1 : 2, overflowX: "hidden" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "space-between", mb: 1 }}>
                {!collapsed && <Typography variant="h6" sx={{ fontWeight: 600 }}>Menù</Typography>}
                {onToggleCollapsed && (
                    <Tooltip title={collapsed ? "Espandi menù" : "Riduci menù"} placement="right">
                        <IconButton size="small" onClick={onToggleCollapsed}>
                            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            <Divider />
            <Stack spacing={1} sx={{ mt: 2, alignItems: collapsed ? "center" : "stretch" }}>
                {items.map((item) =>
                    collapsed ? (
                        <Tooltip key={item.key} title={item.label} placement="right">
                            {/* span: il tooltip deve funzionare anche sui pulsanti disabilitati */}
                            <span>
                                <IconButton
                                    onClick={item.onClick}
                                    disabled={item.disabled}
                                    sx={{
                                        bgcolor: `${item.color ?? "primary"}.main`,
                                        color: "white",
                                        borderRadius: 2,
                                        "&:hover": { bgcolor: `${item.color ?? "primary"}.dark` },
                                        "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
                                    }}
                                >
                                    {item.icon}
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : (
                        <Button
                            key={item.key}
                            fullWidth
                            variant="contained"
                            color={item.color ?? "primary"}
                            startIcon={item.icon}
                            onClick={item.onClick}
                            disabled={item.disabled}
                            sx={{ justifyContent: "flex-start" }}
                        >
                            {item.label}
                        </Button>
                    )
                )}
            </Stack>
        </Box>
    );
}
